/**
 * Where a pending performance actually opens.
 *
 * Mounted once in the shell, beside `WrappedHost` and for the same reasons: a
 * full-screen takeover cannot be owned by whichever screen happened to be
 * visible when it became due, and it must not open over the boot animation —
 * the splash ends by landing the mark on the day's ring, and a dark screen
 * dropping over that is two animations fighting for one second.
 *
 * The moment is recorded on close rather than on open. An interrupted
 * ceremony — the app backgrounded, the phone locked — is offered again next
 * launch, which is what you want from something that only happens once.
 */

import { useEffect } from 'react'
import { markMoment } from '../glitime/moments.js'
import { usePresence } from '../presence/store.js'
import { offerForToday } from './detect.js'
import { MiniCeremony } from './MiniCeremony.js'
import { clearCeremony, useCeremony } from './store.js'

export function CeremonyHost({ ready }: { ready: boolean }) {
  const pending = useCeremony((state) => state.pending)
  const bigDay = usePresence((state) => state.bigDay)
  const sagaFinale = usePresence((state) => state.sagaFinale)
  const presenceReady = usePresence((state) => state.ready)

  // The two that are not about an award. Runs once the diary has been read —
  // before that, `bigDay` and `sagaFinale` are still the store's defaults and
  // would offer nothing, then never be asked again.
  useEffect(() => {
    if (!ready || !presenceReady) return
    void offerForToday({ bigDay, sagaFinale })
  }, [ready, presenceReady, bigDay, sagaFinale])

  if (!ready || pending == null) return null

  return (
    <MiniCeremony
      copy={pending.copy}
      onClose={() => {
        void markMoment(pending.kind, pending.id)
        clearCeremony()
      }}
    />
  )
}
